import React, { ReactElement, cloneElement, isValidElement, useState } from "react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

import { dialogNarrow } from "@/lib/dialogSize";

/**
 * Solid button classes for confirm button color variants.
 */
const confirmButtonColorClasses: Record<string, string> = {
  primary: "bg-primary text-primary-foreground hover:bg-primary/90",
  danger: "bg-destructive text-white hover:bg-destructive/90",
  success:
    "bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600",
  warning:
    "bg-amber-500 text-white hover:bg-amber-600 dark:bg-amber-600 dark:hover:bg-amber-500",
  neutral: "bg-muted text-muted-foreground hover:bg-muted/80",
};

interface ActionConfig {
  title: string;
  reasonLabel: string;
  reasonPlaceholder: string;
  confirmButtonText: string;
  confirmButtonColor?: "primary" | "danger" | "success" | "warning" | "neutral";
  successMessage?: string;
}

interface ActionConfirmationModalProps {
  actionConfig: ActionConfig;
  onConfirm: (reason: string) => Promise<void> | void;
  /** Only `disabled` is used: it is applied to the trigger element. */
  buttonProps?: Record<string, any>;
  /**
   * The element that opens the dialog. It must render a DOM element itself (a Button), because the dialog trigger
   * and the tooltip merge their handlers into it; a wrapper such as a TooltipProvider renders nothing, so the click
   * never reaches the dialog. Use `tooltip` instead of wrapping it.
   */
  children?: ReactElement;
  /** Tooltip shown on the trigger element. */
  tooltip?: React.ReactNode;
  open?: boolean;
  onClose?: () => void;
  /** Optional warning bullets rendered in an amber callout above the reason field. */
  warnings?: string[];
  /** Optional node rendered at the top of the body (e.g. a live status banner). */
  description?: React.ReactNode;
  /** When false, the reason field is hidden and confirmation does not require a reason. Defaults to true. */
  requireReason?: boolean;
}

const ActionConfirmationModal: React.FC<ActionConfirmationModalProps> = ({
  actionConfig,
  onConfirm,
  buttonProps = {},
  children,
  open: controlledOpen,
  onClose: controlledOnClose,
  warnings,
  description,
  requireReason = true,
  tooltip,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm(reason);
      if (actionConfig.successMessage) {
        toast.success(actionConfig.successMessage);
      }
      if (!isControlled) setInternalOpen(false);
      else controlledOnClose?.();
      setReason("");
    } catch (error: any) {
      if (!error?.__suppressToast) {
        toast.error(error?.message || "An unexpected error occurred.", {
          duration: 8000,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isControlled) setInternalOpen(false);
    else controlledOnClose?.();
    setReason("");
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      handleClose();
    } else if (!isControlled) {
      setInternalOpen(true);
    }
  };

  const colorKey = actionConfig.confirmButtonColor || "primary";

  const triggerElement =
    children && isValidElement(children) && buttonProps.disabled
      ? cloneElement(children as ReactElement<{ disabled?: boolean }>, { disabled: true })
      : children;

  const trigger = triggerElement && (
    <DialogTrigger asChild>
      {triggerElement}
    </DialogTrigger>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger && tooltip ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{trigger}</TooltipTrigger>
            <TooltipContent>{tooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        trigger
      )}
      <DialogContent className={`${dialogNarrow} overflow-hidden`}>
          <DialogHeader>
            <DialogTitle>{actionConfig.title}</DialogTitle>
          </DialogHeader>

          <Separator />

          <div className="space-y-3 overflow-y-auto max-h-[60vh]">
            {description && <div>{description}</div>}

            {warnings && warnings.length > 0 && (
              <Alert className="border-amber-500/50 text-amber-900 dark:text-amber-200 [&>svg]:text-amber-600">
                <TriangleAlert />
                <AlertTitle>Before you save</AlertTitle>
                <AlertDescription className="text-amber-900/90 dark:text-amber-200/90">
                  <ul className="list-disc pl-4 space-y-1">
                    {warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {requireReason && (
              <div className="space-y-2">
                <Label>{actionConfig.reasonLabel}:</Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={actionConfig.reasonPlaceholder}
                  rows={4}
                  required
                />
              </div>
            )}
          </div>

          <DialogFooter className="mt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              className="min-w-[100px]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={(requireReason && !reason.trim()) || isSubmitting}
              className={cn("min-w-[100px]", confirmButtonColorClasses[colorKey])}
            >
              {isSubmitting && <Loader2 className="size-4 animate-spin" />}
              {actionConfig.confirmButtonText}
            </Button>
          </DialogFooter>
        </DialogContent>
    </Dialog>
  );
};

export default ActionConfirmationModal;

export const ACTION_CONFIGS = {
  UPDATE_ORGANIZATION_CONFIGURATION: {
    title: "Update Organization Configuration Confirmation",
    reasonLabel: "Update Reason",
    reasonPlaceholder:
      "Please enter the reason for updating organization configuration...",
    confirmButtonText: "Update",
    confirmButtonColor: "primary" as const,
    successMessage: "Organization configuration updated",
  },

  SET_CONFIGURATION_AS_DEFAULT: {
    title: "Set Configuration As Default Confirmation",
    reasonLabel: "Set Reason",
    reasonPlaceholder:
      "Please enter the reason for setting configuration as default...",
    confirmButtonText: "Set As Default",
    confirmButtonColor: "primary" as const,
    successMessage: "Default configuration set",
  },

  CREATE_CLIENT_CONFIGURATION: {
    title: "Create Client Configuration Confirmation",
    reasonLabel: "Create Reason",
    reasonPlaceholder:
      "Please enter the reason for creating client configuration...",
    confirmButtonText: "Create",
    confirmButtonColor: "primary" as const,
    successMessage: "Client configuration created",
  },

  UPDATE_CLIENT_CONFIGURATION: {
    title: "Update Client Configuration Confirmation",
    reasonLabel: "Update Reason",
    reasonPlaceholder:
      "Please enter the reason for updating client configuration...",
    confirmButtonText: "Update",
    confirmButtonColor: "primary" as const,
    successMessage: "Client configuration updated",
  },

  CHANGE_CLIENT_ACTIVE_CONFIGURATION: {
    title: "Change Client Active Configuration Confirmation",
    reasonLabel: "Change Reason",
    reasonPlaceholder:
      "Please enter the reason for changing client active configuration...",
    confirmButtonText: "Change",
    confirmButtonColor: "primary" as const,
    successMessage: "Client active configuration changed",
  },

  // EXPORT_* configs deliberately omit `successMessage`:
  //  - "Create export" tables use this as step 1 of a two-step flow; the
  //    actual export-start mutation toasts itself ("Export started").
  //  - "Status table" downloads open the exported CSV in a new tab — the
  //    browser indicates the download; an additional toast would be noise.
  EXPORT_CASE: {
    title: "Export Case Confirmation",
    reasonLabel: "Export Reason",
    reasonPlaceholder:
      "Please enter the reason for exporting this case...",
    confirmButtonText: "Export",
    confirmButtonColor: "primary" as const,
  },

  EXPORT_CASE_FILES_VIEW: {
    title: "Export Case Files View Confirmation",
    reasonLabel: "Export Reason",
    reasonPlaceholder:
      "Please enter the reason for exporting this case files view...",
    confirmButtonText: "Export",
    confirmButtonColor: "primary" as const,
  },

  EXPORT_INTAKE: {
    title: "Export Intake Confirmation",
    reasonLabel: "Export Reason",
    reasonPlaceholder:
      "Please enter the reason for exporting this intake...",
    confirmButtonText: "Export",
    confirmButtonColor: "primary" as const,
  },

  EXPORT_INTAKE_FILES_VIEW: {
    title: "Export Intake Files View Confirmation",
    reasonLabel: "Export Reason",
    reasonPlaceholder:
      "Please enter the reason for exporting this intake files view...",
    confirmButtonText: "Export",
    confirmButtonColor: "primary" as const,
  },

  EXPORT_PROVIDER: {
    title: "Export Provider Confirmation",
    reasonLabel: "Export Reason",
    reasonPlaceholder:
      "Please enter the reason for exporting this provider...",
    confirmButtonText: "Export",
    confirmButtonColor: "primary" as const,
  },

  EXPORT_PROVIDER_FILES_VIEW: {
    title: "Export Provider Files View Confirmation",
    reasonLabel: "Export Reason",
    reasonPlaceholder:
      "Please enter the reason for exporting this provider files view...",
    confirmButtonText: "Export",
    confirmButtonColor: "primary" as const,
  },

  EXPORT_TEMPLATES_LIBRARY: {
    title: "Export Templates Library Confirmation",
    reasonLabel: "Export Reason",
    reasonPlaceholder:
      "Please enter the reason for exporting this templates library...",
    confirmButtonText: "Export",
    confirmButtonColor: "primary" as const,
  },

  EXPORT_TEMPLATES_LIBRARY_FILES_VIEW: {
    title: "Export Templates Library Files View Confirmation",
    reasonLabel: "Export Reason",
    reasonPlaceholder:
      "Please enter the reason for exporting this templates library files view...",
    confirmButtonText: "Export",
    confirmButtonColor: "primary" as const,
  },

  EXPORT_FIRM_DOCUMENTS_FOLDER: {
    title: "Export Firm Documents Folder Confirmation",
    reasonLabel: "Export Reason",
    reasonPlaceholder:
      "Please enter the reason for exporting this firm documents folder...",
    confirmButtonText: "Export",
    confirmButtonColor: "primary" as const,
  },

  EXPORT_FIRM_DOCUMENTS_VIEW: {
    title: "Export Firm Documents View Confirmation",
    reasonLabel: "Export Reason",
    reasonPlaceholder:
      "Please enter the reason for exporting this firm documents view...",
    confirmButtonText: "Export",
    confirmButtonColor: "primary" as const,
  },

  MARK_NOT_FOUND_FILES_FOR_RETRY_ORG_WIDE: {
    title: "Retry Not Found Files Org-Wide",
    reasonLabel: "Reason",
    reasonPlaceholder:
      "Please enter the reason for retrying the Not Found files...",
    confirmButtonText: "Retry",
    confirmButtonColor: "primary" as const,
  },
  MARK_NOT_FOUND_FILE_FOR_RETRY: {
    title: "Mark Not Found File For Retry Confirmation",
    reasonLabel: "Reason",
    reasonPlaceholder:
      "Please enter the reason for marking this file for retry...",
    confirmButtonText: "Confirm",
    confirmButtonColor: "primary" as const,
    successMessage: "File queued for retry",
  },

  CHANGE_CLIENT_CACHE_SETTINGS: {
    title: "Change Client Cache Settings Confirmation",
    reasonLabel: "Change Reason",
    reasonPlaceholder: "Please enter the reason for changing client cache settings...",
    confirmButtonText: "Change",
    confirmButtonColor: "primary" as const,
    successMessage: "Client cache settings changed",
  },

  UPDATE_FLAGGED_PATH: {
    title: "Update Flagged Path Confirmation",
    reasonLabel: "Update Reason",
    reasonPlaceholder:
      "Please enter the reason for updating this flagged path...",
    confirmButtonText: "Update",
    confirmButtonColor: "primary" as const,
    successMessage: "Flagged path updated",
  },

  REQUEST_FOLDER_SWEEP: {
    title: "Request Folder Sweep Confirmation",
    reasonLabel: "Sweep Reason",
    reasonPlaceholder: "Please enter the reason for requesting a sweep...",
    confirmButtonText: "Request sweep",
    confirmButtonColor: "primary" as const,
  },

  EXPORT_FLAGGED_PATH: {
    title: "Export Flagged Path Confirmation",
    reasonLabel: "Export Reason",
    reasonPlaceholder: "Please enter the reason for exporting this flagged path...",
    confirmButtonText: "Export",
    confirmButtonColor: "primary" as const,
  },

  EXPORT_SOURCE_LINKS: {
    title: "Export Source Links Confirmation",
    reasonLabel: "Export Reason",
    reasonPlaceholder: "Please enter the reason for exporting these source links...",
    confirmButtonText: "Export",
    confirmButtonColor: "primary" as const,
  },

  DOWNLOAD_SOURCE_LINKS_EXPORT: {
    title: "Download Source Links Export",
    reasonLabel: "Download Reason",
    reasonPlaceholder: "Please enter the reason for downloading this export...",
    confirmButtonText: "Download",
    confirmButtonColor: "primary" as const,
  },
} as const;
